import { APP } from '../tokens';
import type { CoTErrorCode } from '../../../../../shared/types';
import { getContextualCoTErrorPresentation, type CoTErrorRenderContext } from '../../../lib/cotErrorPresentation';

const F = APP.fontFamily;

interface CoTErrorCardProps {
  message: string;
  errorCode?: CoTErrorCode;
  toolName?: string;
  errorRenderContext?: CoTErrorRenderContext;
  /** Backend-driven override for retry visibility (V2 query runs). */
  retryableOverride?: boolean;
  /** Retry count label (e.g., "2/3") shown on the Retry button. */
  retryCountLabel?: string;
  /** Inline denial message when a retry/skip was rejected by backend policy. */
  denialMessage?: string;
  onRetry?: () => void;
  onSkip?: () => void;
}

export default function CoTErrorCard({
  message,
  errorCode,
  toolName,
  errorRenderContext,
  retryableOverride,
  retryCountLabel,
  denialMessage,
  onRetry,
  onSkip,
}: CoTErrorCardProps) {
  // Compute contextual presentation — falls back to raw message when no metadata
  const ctx = errorRenderContext ?? { failureCount: 0 };
  const presentation = getContextualCoTErrorPresentation(
    { message, errorCode, toolName },
    ctx,
  );

  // Backend override takes precedence when provided (V2 query runs)
  const showRetry = retryableOverride != null ? retryableOverride : presentation.retryable;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 6,
        paddingBlock: 8,
        paddingInline: 10,
        gap: 8,
        backgroundColor: 'rgba(217, 69, 69, 0.06)',
        border: '1px solid rgba(217, 69, 69, 0.15)',
      }}
    >
      {/* Quota warning — softer, informational */}
      {presentation.quotaWarning && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: '#D97645',
            lineHeight: '14px',
            fontFamily: F,
            backgroundColor: 'rgba(217, 118, 69, 0.06)',
            padding: '3px 8px',
            borderRadius: 4,
          }}
        >
          {presentation.quotaWarning}
        </span>
      )}

      {/* Title — shown when we have contextual metadata */}
      {presentation.title !== 'Something went wrong' && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#D94545',
            lineHeight: '16px',
            fontFamily: F,
          }}
        >
          {presentation.title}
        </span>
      )}

      {/* Body */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 400,
          color: presentation.title !== 'Something went wrong' ? '#8B7D6B' : '#D94545',
          lineHeight: '16px',
          fontFamily: F,
        }}
      >
        {presentation.body}
      </span>

      {/* Denial feedback */}
      {denialMessage && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: '#D97645',
            lineHeight: '14px',
            fontFamily: F,
            backgroundColor: 'rgba(217, 118, 69, 0.06)',
            padding: '3px 8px',
            borderRadius: 4,
          }}
        >
          {denialMessage}
        </span>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {onRetry && showRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 4,
              paddingBlock: 4,
              paddingInline: 10,
              gap: 4,
              backgroundColor: APP.bgCardBody,
              border: '1px solid rgba(217, 69, 69, 0.2)',
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: '#D94545',
              lineHeight: '14px',
            }}
          >
            {retryCountLabel ? `Retry (${retryCountLabel})` : 'Retry'}
          </button>
        )}
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 4,
              paddingBlock: 4,
              paddingInline: 10,
              backgroundColor: 'transparent',
              border: `1px solid ${APP.border}`,
              cursor: 'pointer',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: APP.textCardDetail,
              lineHeight: '14px',
            }}
          >
            Skip step
          </button>
        )}

        {/* CTA — upgrade / admin / support */}
        {presentation.cta && (
          <button
            type="button"
            onClick={() => {
              if (presentation.cta!.intent === 'upgrade') {
                window.open('/pricing', '_blank');
              }
              // 'admin' and 'support' intents are informational — the label is the guidance
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 4,
              paddingBlock: 4,
              paddingInline: 10,
              gap: 4,
              backgroundColor: presentation.cta.intent === 'upgrade' ? '#D97645' : 'transparent',
              border: presentation.cta.intent === 'upgrade' ? 'none' : `1px solid ${APP.border}`,
              cursor: presentation.cta.intent === 'upgrade' ? 'pointer' : 'default',
              fontFamily: F,
              fontSize: 11,
              fontWeight: 500,
              color: presentation.cta.intent === 'upgrade' ? '#FFFFFF' : APP.textCardDetail,
              lineHeight: '14px',
            }}
          >
            {presentation.cta.label}
          </button>
        )}
      </div>
    </div>
  );
}
